import {
  Button,
  Grid,
  IconButton,
  InputAdornment,
  Stack,
  TextField,
  Typography,
  useTheme,
} from '@mui/material';
import React, { useCallback, useState } from 'react';
import Visibility from '@mui/icons-material/Visibility';
import VisibilityOff from '@mui/icons-material/VisibilityOff';
import { useFormik } from 'formik';
import { useNavigate, useParams } from 'react-router-dom';
import * as Yup from 'yup';

import useAuth from '../../hooks/useAuth';
import SwalToast from '../../components/Common/SwalToast';
import { ReactComponent as SolyticsPartnersLogo } from '../../assets/SolyticsPartnersLogo.svg';

const validationSchema = Yup.object().shape({
  newPassword: Yup.string().required('Required'),
  confirmPassword: Yup.string()
    .required('Required')
    .oneOf([Yup.ref('newPassword'), null], 'Passwords must match'),
});

const ResetPassword = () => {
  const { resetPassword } = useAuth();
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const theme = useTheme();

  const handleMouseDownPassword = useCallback(
    (event) => event.preventDefault(),
    []
  );
  const { uid, token, tenant } = useParams();
  const navigate = useNavigate();

  const resetPasswordFormik = useFormik({
    initialValues: {
      newPassword: '',
      confirmPassword: '',
    },
    validationSchema,
    onSubmit: async (values) => {
      const payload = {
        password: values.newPassword,
        uidb64: uid,
        token,
      };

      const response = await resetPassword(
        {
          'X-Tenant-ID': tenant,
        },
        payload
      );
      SwalToast({
        icon: 'success',
        title: response?.data?.message || 'Password reset successfully',
      });

      navigate('/launcher');
    },
  });

  const handleShowVisibility = useCallback(
    () => setShowNewPassword((prev) => !prev),
    []
  );

  return (
    <Grid
      container
      height="100vh"
      sx={{
        backgroundColor: theme.palette?.background?.default,
        zIndex: 1,
        overflowX: 'auto',
      }}
    >
      <Grid
        container
        item
        xs={12}
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          flexDirection: 'column',
          gap: 4,
          height: '100%',
        }}
      >
        <SolyticsPartnersLogo color={theme.palette.primary.main} />

        <Grid
          sx={{
            p: 4,
            width: 450,
            border: `1px solid ${theme.palette.loginPage.loginFormBorder}`,
            borderRadius: 2,
            backgroundColor: theme.palette.loginPage.background,
          }}
        >
          <Stack spacing={5}>
            <Typography variant="h2" fontWeight="bold">
              Reset Password
            </Typography>

            <TextField
              required
              fullWidth
              name="newPassword"
              label="New Password"
              placeholder="Enter your new password"
              type={showNewPassword ? 'text' : 'password'}
              value={resetPasswordFormik.values.newPassword}
              onChange={resetPasswordFormik.handleChange}
              onBlur={resetPasswordFormik.handleBlur}
              error={
                resetPasswordFormik.touched.newPassword &&
                Boolean(resetPasswordFormik.errors.newPassword)
              }
              helperText={
                resetPasswordFormik.touched.newPassword &&
                resetPasswordFormik.errors.newPassword
              }
              variant="outlined"
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      aria-label="toggle new password visibility"
                      onClick={handleShowVisibility}
                      onMouseDown={handleMouseDownPassword}
                      edge="end"
                    >
                      {showNewPassword ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
              InputLabelProps={{
                shrink: true,
              }}
            />

            <TextField
              required
              fullWidth
              name="confirmPassword"
              label="Confirm Password"
              placeholder="Re-enter your new password"
              type={showConfirmPassword ? 'text' : 'password'}
              value={resetPasswordFormik.values.confirmPassword}
              onChange={resetPasswordFormik.handleChange}
              onBlur={resetPasswordFormik.handleBlur}
              error={
                resetPasswordFormik.touched.confirmPassword &&
                Boolean(resetPasswordFormik.errors.confirmPassword)
              }
              helperText={
                resetPasswordFormik.touched.confirmPassword &&
                resetPasswordFormik.errors.confirmPassword
              }
              variant="outlined"
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      aria-label="toggle confirm password visibility"
                      onClick={() => setShowConfirmPassword((prev) => !prev)}
                      onMouseDown={handleMouseDownPassword}
                      edge="end"
                    >
                      {showConfirmPassword ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
              InputLabelProps={{
                shrink: true,
              }}
            />

            <Button
              type="submit"
              variant="contained"
              size="large"
              fullWidth
              onClick={resetPasswordFormik.handleSubmit}
            >
              Reset Password
            </Button>
          </Stack>
        </Grid>
      </Grid>
    </Grid>
  );
};

export default ResetPassword;
